import React, { useState } from "react";
import AppIcon from "../../components/AppIcon";
import LiveActivityMap from "./LiveActivityMap";
import { formatActivityLabel } from "./activityLabels";
import AnalyticsProviderSelect from "./AnalyticsProviderSelect";
import "./analytics.scss";

function Empty({ children }) {
  return (
    <div className="report-empty">
      <AppIcon name="analytics" size={28} />
      <p>{children}</p>
    </div>
  );
}

const genericClicks = new Set(["html", "body", "a", "button", "img", "div", "span", "p", "section", "main", "header", "footer", "nav", "svg", "path", "li", "ul", "ol", "h1", "h2", "h3", "h4", "h5", "h6", "details", "summary", "Page section", "Page visit", "chatbot_launcher"]);

export default function BehaviourReport({
  children,
  report = null,
  controls = null,
  loading = false,
  error = "",
  liveDevice = "desktop",
  onDeviceChange,
  trackingEnabled = true,
  websiteUrl = "",
  aiAnalysis = null,
  aiError = "",
  aiLoading = false,
  aiProvider = null,
  onAnalyze,
}) {
  const [mode, setMode] = useState("heat");
  const [overlay, setOverlay] = useState(true);
  const hasLive = trackingEnabled && Boolean(report?.metrics.pageViews);
  const hasData = hasLive;
  const activeInsights = aiAnalysis?.notes || [];
  const activeClicks = (report?.actions || []).filter(({ label }) => !genericClicks.has(label));
  const topActionCount = Math.max(1, ...activeClicks.map(action => action.count));
  const metrics = [
        ["Tracked sessions", report?.metrics.sessions ?? 0],
        ["Page visits", report?.metrics.pageViews ?? 0],
        ["Total clicks", report?.metrics.clicks ?? 0],
        [
          "Avg. active time",
          report?.metrics.averageSeconds == null
            ? "—"
            : `${Math.floor(report.metrics.averageSeconds / 60)}m ${report.metrics.averageSeconds % 60}s`,
        ],
        [
          "Average reading progress",
          report?.metrics.averageDepth == null
            ? "—"
            : `${report.metrics.averageDepth}%`,
        ],
      ];
  return (
    <div className="module-page behaviour-report">
      <div className="module-page__intro">
        <div>
          <div className="report-eyebrow">UNDERSTAND EVERY INTERACTION</div>
          <h2>Traffidio Visitor Lens</h2>
          <p>
            Explore where visitors click, how far they scroll, and what keeps
            them engaged.
          </p>
        </div>
      </div>
      <div className="report-notice">
        <AppIcon name="ai" />
        <p>
          Reports use recorded page visits and clicks. Engagement notes summarize these measurements. Different page layouts can affect position accuracy.
        </p>
      </div>
      {controls}
      {!trackingEnabled && (
        <div className="report-notice" style={{ marginBottom: "1rem" }}>
          Visitor tracking is currently disabled for this site. Enable tracking in
          Settings to view live visitor analytics.
        </div>
      )}
      {loading && <p role="status">Loading visitor activity…</p>}
      {error && (
        <div className="notice notice--error" role="alert">
          {error}
        </div>
      )}
      {report?.limited && (
        <div className="report-notice">
          Partial report: calculated from the latest 20,000 events for this
          selection.
        </div>
      )}
      {hasData && Number(report.metrics.sessions || 0) < 20 && <div className="report-notice">Early data: only {Number(report.metrics.sessions || 0)} unique tracked sessions in this view. Click and scroll patterns are provisional; use a longer date range before drawing conclusions.</div>}
      <div className="report-heading">
        <div>
          <h3>Visitor activity</h3>
          <p>
            {report?.page || "Choose a tracked page to explore visitor activity"}
          </p>
        </div>
        <span className="report-badge">
          {!trackingEnabled
              ? "TRACKING DISABLED"
              : hasLive
                ? "RECORDED DATA"
                : "AWAITING TRACKING DATA"}
        </span>
      </div>
      <div className="report-layout">
        <section className="report-panel report-map">
          <div className="report-toolbar">
            <div className="report-segments" aria-label="Visualization">
              {[
                ["heat", "Activity intensity"],
                ["click", "Click locations"],
                ["scroll", "Reading progress"],
              ].map(([key, label]) => (
                <button
                  type="button"
                  key={key}
                  aria-pressed={mode === key}
                  onClick={() => setMode(key)}
                >
                  {label}
                </button>
              ))}
            </div>
            <div
              className="report-segments report-devices"
              aria-label="Preview viewport"
            >
              {["desktop", "tablet", "mobile"].map((key) => (
                <button
                  type="button"
                  key={key}
                  disabled={!onDeviceChange}
                  aria-pressed={liveDevice === key}
                  onClick={() => onDeviceChange?.(key)}
                >
                  {key}
                </button>
              ))}
            </div>
          </div>
          <div className="report-map__body">
            {!trackingEnabled ? (
              <Empty>
                Visitor tracking is disabled for this site.
                <br />
                Turn it back on in Settings to view live activity.
              </Empty>
            ) : hasLive ? (
              <LiveActivityMap
                key={`${report.page}:${report.device}`}
                report={report}
                device={liveDevice}
                mode={mode}
                overlay={overlay}
                websiteBase={websiteUrl}
              />
            ) : (
              <Empty>
                {mode === "scroll"
                  ? "Reading progress data is not available yet."
                  : "No recorded page activity for this selection yet."}
              </Empty>
            )}
          </div>
          <footer className="report-map__legend">
            <span>
              <i /> Low activity <b /> High activity
            </span>
            <label>
              <input
                type="checkbox"
                checked={overlay}
                disabled={!hasData}
                onChange={(e) => setOverlay(e.target.checked)}
              />{" "}
              Show overlay
            </label>
          </footer>
        </section>
        <aside className="report-panel report-insights">
          <h3><AppIcon name="ai" /> Engagement notes <span className="report-badge">AI</span></h3>
          <p className="report-insights__intro">
            Ask the selected AI to review this page's recorded visits, clicks, and reading progress.
          </p>
          {aiProvider && <AnalyticsProviderSelect {...aiProvider} />}
          <button type="button" className="report-insights__analyze" disabled={!hasData || aiLoading || !aiProvider?.selection.model} onClick={onAnalyze}><AppIcon name="ai" size={16} />{aiLoading ? "Analyzing page…" : aiAnalysis ? "Refresh AI analysis" : "Analyze this page"}</button>
          {aiError && <p className="notice notice--error" role="alert">{aiError}</p>}
          {activeInsights.length ? (
            <div className="report-insight-list" aria-label="AI engagement findings">
              {activeInsights.map(({ title, body }, index) => (
                <article
                  key={`${title}-${index}`}
                  className="report-insight"
                >
                  <span className="report-insight__number" aria-hidden="true">{String(index + 1).padStart(2, "0")}</span>
                  <div className="report-insight__content">
                    <h4>{title}</h4>
                    <p>{body}</p>
                  </div>
                </article>
              ))}
            </div>
          ) : (
            <Empty>{aiAnalysis?.message || "Run AI analysis to see engagement notes."}</Empty>
          )}
          <div className="report-metrics">
            <h3>Visit summary</h3>
            {metrics.map(([label, value]) => (
              <div key={label}>
                <span>{label}</span>
                <strong>{report ? value : "—"}</strong>
              </div>
            ))}
          </div>
        </aside>
      </div>
      {activeClicks.length > 0 && <div className="report-bottom">
        <section className="report-panel">
          <h3>
            <AppIcon name="analytics" /> Actions on this page
          </h3>
          <p className="report-subtitle">Identifiable links and controls visitors clicked, ranked by count</p>
          <div className="report-ranking">
            {activeClicks.map(({ label, count }, index) => (
              <div key={label}>
                <span>{index + 1}</span>
                <div>
                  <strong>{formatActivityLabel(label)}</strong>
                  <i>
                    <b style={{ width: `${Math.round(count / topActionCount * 100)}%` }} />
                  </i>
                </div>
                <strong>{count}</strong>
              </div>
            ))}
          </div>
        </section>
      </div>}
      {children && <><div className="report-live-heading">
        <h3>Recorded activity</h3>
        <span className="report-badge">LIVE API</span>
      </div>
      {children}</>}
    </div>
  );
}
